import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';
import { taper, yjoint } from './weaver.ts';

/*
 * La Madre Tejedora (the Chasm's Nest guardian, docs/SIMA.md «Guardianes»): «Rompé los capullos antes de que se
 * abran.» The weavers' queen: a spider as big as a cart, eight long legs of black-violet chitin standing her
 * high, a crowned head with eight pale eyes and heavy grey fangs, and behind it an abdomen swollen with eggs,
 * pale violet, streaked with dark chevrons and wound round with white silk, egg-sacs glowing faintly through it.
 * Threads of silk trail from her spinnerets to the floor.
 *
 * How the integrator drives her (anim/creatures/brood-queen.ts): 'lay' (2.0 s): she settles, curls her abdomen
 * down and a cocoon swells at her spinnerets (the `egg` bone, built tiny: full by 1.3 s, set on the floor at
 * 1.55 s and gone by 1.75 s, when the real `cocoon` prop should take its place).
 *
 * Bones: body (the thorax), head, chelL/chelR (the fangs), abdomen, egg, and for each leg leg<i>, knee<i>,
 * foot<i> as on the weaver (legs 0–3 the right side front to back, 4–7 the left; each leg's local +Z runs out
 * along it). Faces +Z.
 */

/** How small the cocoon at her spinnerets is built (the lay clip scales `egg` by 1 / BROOD_EGG_REST). */
export const BROOD_EGG_REST = 0.02;
export const BROOD_QUEEN_HEIGHT = 2.0;

export const broodQueen = (o?: BaseOpts): Model => {
  const c = begin(o, 1.3, 1313);
  const { g, rng } = c;
  const k4 = kit(rng);

  const chitin = std('#1b1622', { roughness: 0.32, metalness: 0.18 });
  const chitinL = std('#3b3049', { roughness: 0.35, metalness: 0.15, flatShading: true });
  const belly = std('#7f7499', { roughness: 0.45, bumpScale: 0.05 });
  const pale = std('#c9bce6', { roughness: 0.5 });
  const silk = std('#e9ecf3', { roughness: 0.9 });
  const fang = std('#8d9198', { roughness: 0.45 });
  const eye = emis('#f6f1ff', '#d9c8ff', 2.2);
  const eggGlow = emis('#e6dcff', '#a98cff', 1.1);
  const thread = basic('#eef0f6', 0.45);

  /* ---------- the thorax ---------- */
  const bodyB = joint(g, V(0, 0.95, 0.3), 'body');
  const bd = bodyB.abs;
  const th = M(new THREE.SphereGeometry(1, 14, 10), chitin, bd);
  th.position.set(0, 0.95, 0.32);
  th.scale.set(0.44, 0.28, 0.52);
  // The carapace: a raised plate with a ridge of short spikes.
  const plate = M(new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), chitinL, bd);
  plate.position.set(0, 1.02, 0.3);
  plate.scale.set(0.36, 0.2, 0.44);
  for (let i = 0; i < 4; i++) {
    const sp = M(new THREE.ConeGeometry(0.035, 0.14, 5), chitinL, bd);
    sp.position.set(0, 1.2 - i * 0.015, 0.5 - i * 0.14);
    sp.rotation.x = -0.5;
  }
  // The underside, pale.
  const under = M(new THREE.SphereGeometry(1, 10, 6), belly, bd);
  under.position.set(0, 0.86, 0.32);
  under.scale.set(0.36, 0.16, 0.42);

  /* ---------- the head: a crown, eight eyes, the fangs ---------- */
  const headB = joint(bd, V(0, 0.98, 0.72), 'head');
  const hd = headB.abs;
  const skull = M(new THREE.SphereGeometry(1, 12, 8), chitin, hd);
  skull.position.set(0, 1.0, 0.86);
  skull.scale.set(0.3, 0.25, 0.27);
  for (const [x, y, z, r] of [[-0.07, 1.06, 1.08, 0.045], [0.07, 1.06, 1.08, 0.045], [-0.15, 1.1, 1.0, 0.03], [0.15, 1.1, 1.0, 0.03], [-0.06, 1.15, 1.03, 0.025], [0.06, 1.15, 1.03, 0.025], [-0.17, 1.02, 1.02, 0.022], [0.17, 1.02, 1.02, 0.022]] as const) {
    M(new THREE.SphereGeometry(r, 7, 5), eye, hd).position.set(x, y, z);
  }
  glowSprite(hd, '#d9c8ff', 0.5, V(0, 1.08, 1.12), 0.55);
  // The crown: pale spikes rising behind the eyes.
  for (let i = 0; i < 5; i++) {
    const a = (i - 2) * 0.38;
    const sp = M(new THREE.ConeGeometry(0.045, 0.42 - Math.abs(i - 2) * 0.07, 5), pale, hd);
    sp.position.set(Math.sin(a) * 0.19, 1.22, 0.84 - Math.cos(a) * 0.04);
    sp.rotation.set(-0.35, 0, -a * 0.8);
  }
  const fangs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const cB = joint(hd, V(k * 0.08, 0.94, 1.05), `chel${s}`);
    const base = M(new THREE.SphereGeometry(1, 8, 6), chitinL, cB.abs);
    base.position.set(k * 0.08, 0.9, 1.07);
    base.scale.set(0.07, 0.11, 0.08);
    M(taper([V(k * 0.08, 0.84, 1.1), V(k * 0.06, 0.72, 1.16), V(k * 0.02, 0.64, 1.12)], 0.04, 0.012, 5, 5), fang, cB.abs);
    fangs[`chel${s}`] = cB.p;
  }

  /* ---------- the abdomen: swollen with eggs, wound with silk ---------- */
  const abdB = joint(bd, V(0, 1.0, -0.1), 'abdomen');
  const ab = abdB.abs;
  const AB = V(0, 1.3, -0.95);
  const sac = M(new THREE.SphereGeometry(1, 14, 10), belly, ab);
  sac.position.copy(AB);
  sac.scale.set(0.92, 0.82, 1.2);
  // Dark chevrons down the top.
  for (let i = 0; i < 5; i++) {
    for (const k of [-1, 1]) {
      const ch = M(new THREE.SphereGeometry(1, 6, 4), chitin, ab);
      const z = -0.35 - i * 0.28;
      const u = (z - AB.z) / 1.2;
      const top = AB.y + 0.82 * Math.sqrt(Math.max(0, 1 - u * u));
      ch.position.set(k * 0.14, top - 0.02 - 0.01 * i, z);
      ch.scale.set(0.2 - i * 0.02, 0.03, 0.06);
      ch.rotation.y = k * 0.6;
    }
  }
  // A row of pale spines down her back, and tufts of hard bristle on her flanks.
  for (let i = 0; i < 6; i++) {
    const z = -0.25 - i * 0.28;
    const u = (z - AB.z) / 1.2;
    const top = AB.y + 0.82 * Math.sqrt(Math.max(0, 1 - u * u));
    const sp = M(new THREE.ConeGeometry(0.04, 0.2 - Math.abs(i - 2.5) * 0.025, 5), pale, ab);
    sp.position.set(0, top + 0.05, z);
    sp.rotation.x = -0.5 - i * 0.12;
  }
  for (let i = 0; i < 10; i++) {
    const a = rng() * Math.PI * 2, z = AB.z + (rng() - 0.5) * 1.6;
    const u = (z - AB.z) / 1.2, rr = Math.sqrt(Math.max(0, 1 - u * u));
    const tuft = M(new THREE.ConeGeometry(0.02, 0.1, 4), chitin, ab);
    const n = V(Math.cos(a) * 0.92 * rr, Math.sin(a) * 0.82 * rr, 0);
    tuft.position.set(n.x, AB.y + n.y, z);
    tuft.quaternion.setFromUnitVectors(V(0, 1, 0), n.clone().normalize());
  }
  // Silk bands wound round it.
  for (const [rx, ry, z, s] of [[0.3, 0.2, -0.55, 0.86], [-0.2, -0.3, -1.05, 0.95], [0.5, 0.1, -1.5, 0.7], [-0.6, 0.5, -0.8, 0.98], [0.15, -0.7, -1.3, 0.85]] as const) {
    const band = M(new THREE.TorusGeometry(1, 0.025, 3, 18), silk, ab);
    band.position.set(0, AB.y, z);
    band.scale.set(0.93 * s, 0.84 * s, 1);
    band.rotation.set(rx, ry, 0);
  }
  // Egg-sacs bulging out of her flanks under a web of silk.
  for (let i = 0; i < 6; i++) {
    const k = i % 2 ? 1 : -1;
    const z = -0.6 - Math.floor(i / 2) * 0.35;
    const egg = M(new THREE.SphereGeometry(0.08 - Math.floor(i / 2) * 0.01, 7, 5), eggGlow, ab);
    egg.position.set(k * 0.86 * Math.sqrt(Math.max(0, 1 - ((z - AB.z) / 1.2) ** 2)), AB.y - 0.12 + (i % 3) * 0.06, z);
    glowSprite(ab, '#c9b4ff', 0.3, egg.position.clone().add(V(k * 0.05, 0, 0)), 0.22);
    const web = M(new THREE.SphereGeometry(0.11, 6, 4), thread, ab);
    web.position.copy(egg.position);
  }
  // The spinnerets, and threads of silk trailing from them to the floor.
  const spin = V(0, 1.06, -2.12);
  const sp = M(new THREE.ConeGeometry(0.12, 0.2, 7), chitinL, ab);
  sp.position.copy(spin).add(V(0, 0, 0.05));
  sp.rotation.x = -Math.PI / 2 - 0.3;
  for (let i = 0; i < 3; i++) {
    const end = V((i - 1) * 0.25, 0.01, -2.5 - i * 0.12);
    k4.tube(ab, [spin, spin.clone().lerp(end, 0.5).add(V(0, -0.15, 0)), end], 0.008, thread, 3);
  }

  /* ---------- the cocoon she lays (built tiny) ---------- */
  const eggB = joint(ab, spin.clone().add(V(0, -0.05, -0.02)), 'egg');
  {
    const eg = G(eggB.abs, spin.x, spin.y - 0.05, spin.z - 0.02);
    eg.scale.setScalar(BROOD_EGG_REST * 1.6);
    const co = M(new THREE.SphereGeometry(1, 10, 8), silk, eg);
    co.position.set(0, -0.3, 0);
    co.scale.set(0.24, 0.32, 0.24);
    for (let i = 0; i < 4; i++) {
      const band = M(new THREE.TorusGeometry(0.22, 0.015, 3, 12), silk, eg);
      band.position.set(0, -0.42 + i * 0.08, 0);
      band.rotation.set(Math.PI / 2 + (i - 1.5) * 0.25, 0, i * 0.4);
    }
    M(new THREE.SphereGeometry(0.1, 6, 4), eggGlow, eg).position.set(0, -0.3, 0.15);
  }

  /* ---------- legs: eight, long, standing her high ---------- */
  const legBones: Record<string, THREE.Object3D> = {};
  const ANG = [0.55, 1.2, 1.9, 2.5];
  const REACH = [1.12, 1.0, 0.95, 1.06];
  for (let i = 0; i < 8; i++) {
    const k = i < 4 ? 1 : -1;
    const row = i % 4;
    const a = k * ANG[row]!;
    const yaw = a;
    const d = V(Math.sin(a), 0, Math.cos(a));
    const hip = V(d.x * 0.36, 0.95, 0.32 + d.z * 0.42);
    const R = REACH[row]!;
    const P = (r: number, y: number): THREE.Vector3 => hip.clone().addScaledVector(d, r * R).setY(y);
    const knee = P(0.75, 1.85 - row * 0.05), ankle = P(1.5, 0.95), toe = P(2.0, 0.02);
    const thB = yjoint(bd, hip, yaw, `leg${i}`);
    const knB = yjoint(thB.abs, knee, yaw, `knee${i}`);
    const ftB = yjoint(knB.abs, ankle, yaw, `foot${i}`);
    legBones[`leg${i}`] = thB.p;
    legBones[`knee${i}`] = knB.p;
    legBones[`foot${i}`] = ftB.p;
    const fm = hip.clone().lerp(knee, 0.5).add(V(0, 0.1, 0)).addScaledVector(d, -0.06);
    M(taper([hip, fm, knee], 0.085, 0.06, 4, 5, 0.85, 0.2), chitin, thB.abs);
    const kn = M(new THREE.SphereGeometry(0.065, 6, 4), chitinL, knB.abs);
    kn.position.copy(knee);
    const tm = knee.clone().lerp(ankle, 0.5).addScaledVector(d, 0.05).add(V(0, 0.06, 0));
    M(taper([knee, tm, ankle], 0.06, 0.045, 4, 5, 1, 0.2), chitin, knB.abs);
    // A pale band at the ankle, then the last segment down to the point.
    const band = M(new THREE.SphereGeometry(0.05, 6, 4), belly, ftB.abs);
    band.position.copy(ankle);
    band.scale.set(1, 1.3, 1);
    M(taper([ankle, ankle.clone().lerp(toe, 0.5).addScaledVector(d, 0.04), toe], 0.042, 0.016, 4, 5, 1, 2), chitinL, ftB.abs);
  }

  return done(c, BROOD_QUEEN_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, head: headB.p, chelL: fangs.chelL!, chelR: fangs.chelR!, abdomen: abdB.p, egg: eggB.p, ...legBones,
  }));
};
